import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { HomeRedirect, LocationSettingsSync } from './components/HomeRedirect'
import { Box, CircularProgress } from '@mui/material'
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import { ProjectProvider } from './contexts/ProjectContext'
import { RepositoriesProvider } from './contexts/RepositoriesContext'
import { CustomThemeProvider } from './contexts/ThemeContext'
import { RoleAssumptionProvider } from './contexts/RoleAssumptionContext'
import { PushNotificationProvider } from './contexts/PushNotificationContext'
import { useAuth } from './contexts/AuthContext'
import PushNotificationBanner from './components/PushNotificationBanner'
import { API_URL } from './config'
import './App.css'

const Home = lazy(() => import('./pages/Home'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const OnboardingSuccess = lazy(() => import('./pages/OnboardingSuccess'))
const Billing = lazy(() => import('./pages/Billing'))
const Databases = lazy(() => import('./pages/Databases'))
const DatabaseOnboarding = lazy(() => import('./pages/DatabaseOnboarding'))
const DatabaseDetail = lazy(() => import('./pages/DatabaseDetail'))
const DatabaseSchemaPage = lazy(() => import('./pages/DatabaseSchemaPage'))
const DatabaseClassificationSuggestionsPage = lazy(() => import('./pages/DatabaseClassificationSuggestionsPage'))
const DatabaseRulesPage = lazy(() => import('./pages/DatabaseRulesPage'))
const DatabaseSettings = lazy(() => import('./pages/DatabaseSettings'))
const DatabaseRoles = lazy(() => import('./pages/DatabaseRoles'))
const DatabaseRoleDetail = lazy(() => import('./pages/DatabaseRoleDetail'))
const DatabaseRoleRuleDetail = lazy(() => import('./pages/DatabaseRoleRuleDetail'))
const DatabaseUsers = lazy(() => import('./pages/DatabaseUsers'))
const DatabaseUserDetail = lazy(() => import('./pages/DatabaseUserDetail'))
const ProxyApiKeySetup = lazy(() => import('./pages/ProxyApiKeySetup'))
const Login = lazy(() => import('./pages/Login'))
const RepositoryDetail = lazy(() => import('./pages/RepositoryDetail'))
const Settings = lazy(() => import('./pages/Settings'))
const Domains = lazy(() => import('./pages/Domains'))
const Projects = lazy(() => import('./pages/Projects'))
const ProjectOnboarding = lazy(() => import('./pages/ProjectOnboarding'))
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'))
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'))
const ServiceSettings = lazy(() => import('./pages/ServiceSettings'))
const CompositeEnvironmentDetail = lazy(() => import('./pages/CompositeEnvironmentDetail'))
const CompositeEnvironmentLogs = lazy(() => import('./pages/CompositeEnvironmentLogs'))
const CompositeEnvironmentDeployments = lazy(() => import('./pages/CompositeEnvironmentDeployments'))
const CompositeEnvironmentQAChecks = lazy(() => import('./pages/CompositeEnvironmentQAChecks'))
const CompositeEnvironmentSettings = lazy(() => import('./pages/CompositeEnvironmentSettings'))
const CompositeEnvironmentServiceSettings = lazy(() => import('./pages/CompositeEnvironmentServiceSettings'))
const QualityMaxDetails = lazy(() => import('./pages/QualityMaxDetails'))
const MCPInstructions = lazy(() => import('./pages/MCPInstructions'))
const DeploymentLogs = lazy(() => import('./pages/DeploymentLogs'))
const Deployments = lazy(() => import('./pages/Deployments'))
const DeploymentDetail = lazy(() => import('./pages/DeploymentDetail'))
const DeploymentMultiRepoDebug = lazy(() => import('./pages/DeploymentMultiRepoDebug'))
const DeploymentEvents = lazy(() => import('./pages/DeploymentEvents'))
const EnvironmentLogs = lazy(() => import('./pages/EnvironmentLogs'))
const EnvironmentDashboard = lazy(() => import('./pages/EnvironmentDashboard'))
const EnvironmentSettingsPage = lazy(() => import('./pages/EnvironmentSettingsPage'))
const RepositorySettings = lazy(() => import('./pages/RepositorySettings'))
const RepositoryLayout = lazy(() => import('./components/RepositoryLayout'))
const PleaseWait = lazy(() => import('./pages/PleaseWait'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const Feedback = lazy(() => import('./pages/Feedback'))

function RoutePageFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
      <CircularProgress />
    </Box>
  )
}

function DatabaseGraphicalRedirect() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}?tab=diagram`} replace />
}

function RedirectDatabaseSensitiveColumnsToDetailTab() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}?tab=sensitive-columns`} replace />
}

function RedirectDatabasePreviewQueriesToDetailTab() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}?tab=preview-queries`} replace />
}

const App: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const getToken = () => localStorage.getItem('github_access_token')

  // Update page title based on environment
  useEffect(() => {
    const isDevEnvironment = API_URL.includes('veilstreamdev.com') || API_URL.includes('localhost')
    document.title = isDevEnvironment ? 'VeilStream - Dev' : 'VeilStream'
  }, [])

  return (
    <CustomThemeProvider>
      <RoleAssumptionProvider>
        <ProjectProvider>
          <RepositoriesProvider>
          <Router>
          <PushNotificationProvider isAuthenticated={isAuthenticated} getToken={getToken}>
          <Box sx={{
            minHeight: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <LocationSettingsSync />
            <Navigation />
            <PushNotificationBanner />
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <Sidebar />
              <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
              <Suspense fallback={<RoutePageFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/tos" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/deploying" element={<PleaseWait />} />
                <Route path="/please-wait" element={<PleaseWait />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <HomeRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/home" element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                } />

                <Route path="/repositories" element={<Navigate to="/projects" replace />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/success" element={
                  <ProtectedRoute>
                    <OnboardingSuccess />
                  </ProtectedRoute>
                } />
                <Route path="/billing" element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/domains" element={
                  <ProtectedRoute>
                    <Domains />
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                } />
                <Route path="/projects/new" element={
                  <ProtectedRoute>
                    <ProjectOnboarding />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id" element={
                  <ProtectedRoute>
                    <ProjectDetail />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/settings" element={
                  <ProtectedRoute>
                    <ProjectSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/service/:mappingId/settings" element={
                  <ProtectedRoute>
                    <ServiceSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentDetail />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/logs" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/deployments" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentDeployments />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/qa-checks" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentQAChecks />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/settings" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/service/:mappingId/settings" element={
                  <ProtectedRoute>
                    <CompositeEnvironmentServiceSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/qa_check/:qaCheckId" element={
                  <ProtectedRoute>
                    <QualityMaxDetails />
                  </ProtectedRoute>
                } />
                <Route path="/settings/mcp-instructions" element={
                  <ProtectedRoute>
                    <MCPInstructions />
                  </ProtectedRoute>
                } />
                <Route path="/feedback" element={
                  <ProtectedRoute>
                    <Feedback />
                  </ProtectedRoute>
                } />
                <Route path="/databases" element={
                  <ProtectedRoute>
                    <Databases />
                  </ProtectedRoute>
                } />
                <Route path="/database/onboarding" element={
                  <ProtectedRoute>
                    <DatabaseOnboarding />
                  </ProtectedRoute>
                } />
                      <Route path="/databases/roles" element={
                        <ProtectedRoute>
                          <DatabaseRoles />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/rules" element={
                        <ProtectedRoute>
                          <Navigate to="/databases" replace />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/rules/new" element={
                        <ProtectedRoute>
                          <Navigate to="/databases" replace />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:id" element={
                        <ProtectedRoute>
                          <DatabaseRoleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:roleId/rules/new" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:roleId/rules/:ruleId" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/:databaseId/rules/new" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/:databaseId/rules/:ruleId" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                <Route path="/databases/users" element={
                  <ProtectedRoute>
                    <DatabaseUsers />
                  </ProtectedRoute>
                } />
                <Route path="/databases/user/:id" element={
                  <ProtectedRoute>
                    <DatabaseUserDetail />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/proxy-api-key/setup" element={
                  <ProtectedRoute>
                    <ProxyApiKeySetup />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/settings" element={
                  <ProtectedRoute>
                    <DatabaseSettings />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/preview-queries" element={
                  <ProtectedRoute>
                    <RedirectDatabasePreviewQueriesToDetailTab />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/sensitive-columns" element={
                  <ProtectedRoute>
                    <RedirectDatabaseSensitiveColumnsToDetailTab />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/classification-suggestions" element={
                  <ProtectedRoute>
                    <DatabaseClassificationSuggestionsPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/schema" element={
                  <ProtectedRoute>
                    <DatabaseSchemaPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/graphical" element={
                  <ProtectedRoute>
                    <DatabaseGraphicalRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/rules" element={
                  <ProtectedRoute>
                    <DatabaseRulesPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id" element={
                  <ProtectedRoute>
                    <DatabaseDetail />
                  </ProtectedRoute>
                } />

                <Route path="/repository/:id" element={
                  <ProtectedRoute>
                    <RepositoryLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<RepositoryDetail />} />
                  <Route path="settings" element={<RepositorySettings />} />
                </Route>
                <Route path="/repository/:id/environment/:environmentId" element={
                  <ProtectedRoute>
                    <EnvironmentDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:id/environment/:environmentId/deployments" element={
                  <ProtectedRoute>
                    <Deployments />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:id/environment/:environmentId/settings" element={
                  <ProtectedRoute>
                    <EnvironmentSettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:id/environment/:environmentId/logs" element={
                  <ProtectedRoute>
                    <EnvironmentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/deployment/:deploymentId/multi-repo-debug" element={
                  <ProtectedRoute>
                    <DeploymentMultiRepoDebug />
                  </ProtectedRoute>
                } />
                <Route path="/deployment/:deploymentId/logs" element={
                  <ProtectedRoute>
                    <DeploymentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/deployment/:deploymentId" element={
                  <ProtectedRoute>
                    <DeploymentDetail />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:repositoryId/environment/:environmentId/deployment/:deploymentId" element={
                  <ProtectedRoute>
                    <DeploymentDetail />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:id/environment/:environmentId/deployment/:deploymentId/logs" element={
                  <ProtectedRoute>
                    <DeploymentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/repository/:id/environment/:environmentId/deployment/:deploymentId/events" element={
                  <ProtectedRoute>
                    <DeploymentEvents />
                  </ProtectedRoute>
                } />
              </Routes>
              </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
          </PushNotificationProvider>
          </Router>
          </RepositoriesProvider>
        </ProjectProvider>
      </RoleAssumptionProvider>
    </CustomThemeProvider>
  )
}

export default App
